import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteAdminSite,
  getAdminSites,
  updateAdminSite,
  type AdminPageMeta,
  type AdminSiteItem,
} from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  ExternalLink,
  EyeOff,
  Eye,
  Search,
  Trash2,
} from "lucide-react";
import { SortableHead } from "@/components/admin/SortableHead";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/sites")({
  component: AdminSites,
});

type SortKey = "createdAt" | "viewsCount" | "username";

// sensible starting direction the first time a column is clicked
const DEFAULT_ORDER: Record<SortKey, "asc" | "desc"> = {
  username: "asc",
  viewsCount: "desc",
  createdAt: "desc",
};

const STATUS_LABELS: Record<string, string> = {
  all: "all",
  published: "published",
  draft: "drafts",
};

function AdminSites() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState<"all" | "published" | "draft">("all");
  const [sortBy, setSortBy] = useState<SortKey>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [deleteTarget, setDeleteTarget] = useState<AdminSiteItem | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "admin-sites",
      { page, pageSize, search, status, sortBy, sortOrder },
    ],
    queryFn: async () => {
      const res = await getAdminSites({
        page,
        pageSize,
        search,
        status,
        sortBy,
        sortOrder,
      });
      if (!res.ok) throw new Error("failed to load sites");
      return (await res.json()) as { items: AdminSiteItem[]; page: AdminPageMeta };
    },
    // keep the current rows visible while the next sort/filter/page loads
    placeholderData: keepPreviousData,
  });

  const publishMutation = useMutation({
    mutationFn: async ({ id, isPublished }: { id: string; isPublished: boolean }) => {
      const res = await updateAdminSite(id, { isPublished });
      if (!res.ok) throw new Error((await res.json())?.error || "failed to update");
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin-sites"] });
      toast.success(vars.isPublished ? "site published" : "site unpublished");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await deleteAdminSite(id);
      if (!res.ok) throw new Error((await res.json())?.error || "failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sites"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      toast.success("site deleted");
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submitSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleSort = (column: string) => {
    const col = column as SortKey;
    if (sortBy === col) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortOrder(DEFAULT_ORDER[col] ?? "desc");
    }
    setPage(1);
  };

  const meta = data?.page;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">sites</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search className="h-4 w-4 text-gray-400" />
            </span>
            <Input
              placeholder="search slug or owner"
              className="w-56 rounded-2xl bg-white/70 pl-9"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitSearch()}
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-2xl gap-2">
                status: {STATUS_LABELS[status]}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(Object.keys(STATUS_LABELS) as Array<typeof status>).map((key) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => {
                    setStatus(key);
                    setPage(1);
                  }}
                >
                  {STATUS_LABELS[key]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Card className="bg-white/70 backdrop-blur-sm border-amber-200/50 rounded-3xl shadow-lg">
        <CardContent
          className={`p-4 transition-opacity duration-200 ${
            isFetching && !isLoading ? "opacity-60" : ""
          }`}
          aria-busy={isFetching}
        >
          {isLoading ? (
            <div className="space-y-2 py-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-10 bg-amber-100/40 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : !data?.items.length ? (
            <div className="py-12 text-center text-gray-500">no sites found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-amber-200/50">
                  <SortableHead
                    label="site"
                    column="username"
                    activeColumn={sortBy}
                    order={sortOrder}
                    onSort={handleSort}
                  />
                  <TableHead>owner</TableHead>
                  <TableHead>status</TableHead>
                  <SortableHead
                    label="views"
                    column="viewsCount"
                    activeColumn={sortBy}
                    order={sortOrder}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <TableHead>format</TableHead>
                  <SortableHead
                    label="created"
                    column="createdAt"
                    activeColumn={sortBy}
                    order={sortOrder}
                    onSort={handleSort}
                  />
                  <TableHead className="text-right">actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((s) => {
                  const published = s.isPublished === "true";
                  return (
                    <TableRow key={s.id} className="border-amber-100/60">
                      <TableCell>
                        <div className="font-medium text-amber-700">
                          {s.isDraft ? (
                            <span className="text-gray-400">draft ({s.id})</span>
                          ) : (
                            `clove.moe/${s.username}`
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-gray-900">
                          {s.ownerDisplayUsername || s.ownerUsername || "—"}
                        </div>
                        <div className="text-xs text-gray-500">{s.ownerEmail}</div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={published ? "default" : "secondary"}
                          className={
                            published
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-gray-100 text-gray-600 border-gray-200"
                          }
                        >
                          {published ? "published" : "draft"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {s.viewsCount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-gray-600">{s.bioFormat || "bml"}</TableCell>
                      <TableCell className="text-gray-600">
                        {new Date(s.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl hover:bg-amber-100"
                            disabled={s.isDraft || !published}
                            title="open site"
                            onClick={() => window.open(`/${s.username}`, "_blank")}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl hover:bg-amber-100"
                            disabled={publishMutation.isPending}
                            title={published ? "unpublish" : "publish"}
                            onClick={() =>
                              publishMutation.mutate({ id: s.id, isPublished: !published })
                            }
                          >
                            {published ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl hover:bg-red-100 text-red-600"
                            title="delete site"
                            onClick={() => setDeleteTarget(s)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {meta && meta.total > 0 && (
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-gray-600">
                showing {(meta.page - 1) * meta.pageSize + 1}-
                {Math.min(meta.page * meta.pageSize, meta.total)} of {meta.total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={meta.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  previous
                </Button>
                <div className="text-sm text-gray-700">
                  page {meta.page} of {meta.totalPages}
                </div>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={meta.page >= meta.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>delete site</DialogTitle>
            <DialogDescription>
              {deleteTarget?.isDraft
                ? "delete this draft?"
                : `delete clove.moe/${deleteTarget?.username}?`}{" "}
              the owner will lose this page permanently. this cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleteMutation.isPending || !deleteTarget}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? "deleting..." : "delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
